import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { MetricsSummary } from "@akhshy/types";
import { api } from "../lib/api.ts";
import { errorMessage } from "../lib/errors.ts";
import { LoadingState, ErrorState } from "../components/molecules/StateBlock.tsx";

interface Counts {
  label: string;
  to: string;
  total: number;
  published: number;
}

/** Count-only fetch: pull just totals, never the full list. */
async function loadCounts(): Promise<Counts[]> {
  const totals = { page: 1, pageSize: 1, includeUnpublished: true };
  const published = { page: 1, pageSize: 1, includeUnpublished: false };
  const [
    vaultAll,
    vaultPub,
    promptsAll,
    promptsPub,
    ritualsAll,
    ritualsPub,
    projectsAll,
    projectsPub,
  ] = await Promise.all([
    api.vault.list(totals),
    api.vault.list(published),
    api.prompts.list(totals),
    api.prompts.list(published),
    api.rituals.list(totals),
    api.rituals.list(published),
    api.projects.list(totals),
    api.projects.list(published),
  ]);
  const stat = (
    label: string,
    to: string,
    all: { total: number },
    pub: { total: number },
  ): Counts => ({ label, to, total: all.total, published: pub.total });
  return [
    stat("Vault entries", "/vault", vaultAll, vaultPub),
    stat("Prompts", "/prompts", promptsAll, promptsPub),
    stat("Ritual entries", "/rituals", ritualsAll, ritualsPub),
    stat("Projects", "/projects", projectsAll, projectsPub),
  ];
}

export function OverviewPage() {
  const [counts, setCounts] = useState<Counts[] | null>(null);
  const [metrics, setMetrics] = useState<MetricsSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setError(null);
    setCounts(null);
    setMetrics(null);
    Promise.all([loadCounts(), api.metrics.summary()])
      .then(([c, m]) => {
        setCounts(c);
        setMetrics(m);
      })
      .catch((err) => setError(errorMessage(err)));
  };

  useEffect(load, []);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!counts || !metrics) return <LoadingState />;

  return (
    <>
      <div className="overview-grid">
        {counts.map((c) => (
          <Link key={c.to} className="stat-card" to={c.to}>
            <div className="stat-num">{c.total}</div>
            <div className="stat-label">{c.label}</div>
            <div className="stat-sub">{c.published} published</div>
          </Link>
        ))}
        <div className="stat-card">
          <div className="stat-num">{metrics.prompts.totalCopies}</div>
          <div className="stat-label">Prompt copies</div>
          <div className="stat-sub">{metrics.vault.totalOpens} vault opens</div>
        </div>
      </div>

      <section className="metrics-panel">
        <h2 className="metrics-heading">Most reached for</h2>
        <div className="metrics-cols">
          <MetricList
            title="Top prompts (copied)"
            empty="No copies recorded yet."
            rows={metrics.prompts.top}
          />
          <MetricList
            title="Top vault links (opened)"
            empty="No opens recorded yet."
            rows={metrics.vault.top}
          />
        </div>
      </section>
    </>
  );
}

function MetricList({
  title,
  empty,
  rows,
}: {
  title: string;
  empty: string;
  rows: MetricsSummary["prompts"]["top"];
}) {
  return (
    <div className="metric-list">
      <h3 className="metric-list-title">{title}</h3>
      {rows.length === 0 ? (
        <p className="metric-empty">{empty}</p>
      ) : (
        <ol className="metric-rows">
          {rows.map((r) => (
            <li key={r.id} className="metric-row">
              <span className="metric-row-label">
                {r.useCase ? <em>{r.useCase} · </em> : null}
                {r.label}
              </span>
              <span className="metric-row-count">{r.count}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
